import { useEffect, useState } from 'react'
import { Head } from '@inertiajs/react'
import { motion as Motion, useSpring, useTransform } from 'motion/react'
import {
  Clock,
  TrendingUp,
  ArrowRight,
  PiggyBank,
  Wrench,
  Activity,
  Mail,
  Phone,
  Building,
  User,
  Send,
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast, Toaster } from 'sonner'
// --- Types ---
interface ContactFormData {
  name: string
  email: string
  company: string
  phone: string
  assets: string
}

// --- Animated Number Component ---
function AnimatedNumber({
  value,
  currency = true,
}: {
  value: number
  currency?: boolean
}) {
  const formatted = currency
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(value)
    : Math.round(value).toLocaleString()

  const spring = useSpring(0, {
    mass: 0.8,
    stiffness: 75,
    damping: 15,
  })
  const display = useTransform(spring, (current) => {
    const clamped = Math.max(0, current)
    return currency
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        }).format(clamped)
      : Math.round(clamped).toLocaleString()
  })

  useEffect(() => {
    spring.set(value)
  }, [value, spring])

  return (
    <span>
      <Motion.span aria-hidden="true">{display}</Motion.span>
      <span className="sr-only">{formatted}</span>
    </span>
  )
}

// --- Contact Form Component ---
function ContactForm({ prefilledAssets }: { prefilledAssets: string }) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    defaultValues: {
      assets: prefilledAssets,
    },
  })

  // Sync assets if user changes calculator
  useEffect(() => {
    reset((prev) => ({ ...prev, assets: prefilledAssets }))
  }, [prefilledAssets, reset])

  const onSubmit = async (data: ContactFormData) => {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

    const response = await fetch('/inquiries', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      },
      body: JSON.stringify({ inquiry: data }),
    })

    if (response.ok) {
      toast.success('Thanks for reaching out! A CurbTek representative will contact you soon.')
      reset()
    } else {
      const body = await response.json().catch(() => null)
      const message = body?.errors?.join(', ') || 'Something went wrong. Please try again.'
      toast.error(message)
    }
  }

  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
      <div className="bg-brand-dark rounded-3xl overflow-hidden shadow-2xl border border-white/5 relative">
        {/* Background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-brand-red/10 blur-[120px] pointer-events-none"></div>

        <div className="relative p-8 md:p-12 lg:p-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Contact us to <span className="text-brand-red">learn more</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              Ready to see the full potential of CurbTek for your business? Send us your details and we'll be in touch.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div className="space-y-2">
                <label htmlFor="contact-name" className="text-sm font-semibold text-gray-300 ml-1">Full Name</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" aria-hidden="true">
                    <User className="h-5 w-5 text-gray-500" />
                  </div>
                  <input
                    {...register('name', { required: 'Name is required' })}
                    id="contact-name"
                    type="text"
                    placeholder="John Doe"
                    className="block w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-brand-red focus:ring-4 focus:ring-brand-red/10 transition-all font-medium"
                  />
                </div>
                {errors.name && <p className="text-brand-red text-xs mt-1 ml-1">{errors.name.message}</p>}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label htmlFor="contact-email" className="text-sm font-semibold text-gray-300 ml-1">Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" aria-hidden="true">
                    <Mail className="h-5 w-5 text-gray-500" />
                  </div>
                  <input
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                    id="contact-email"
                    type="email"
                    placeholder="john@company.com"
                    className="block w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-brand-red focus:ring-4 focus:ring-brand-red/10 transition-all font-medium"
                  />
                </div>
                {errors.email && <p className="text-brand-red text-xs mt-1 ml-1">{errors.email.message}</p>}
              </div>

              {/* Company */}
              <div className="space-y-2">
                <label htmlFor="contact-company" className="text-sm font-semibold text-gray-300 ml-1">Company Name</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" aria-hidden="true">
                    <Building className="h-5 w-5 text-gray-500" />
                  </div>
                  <input
                    {...register('company', { required: 'Company is required' })}
                    id="contact-company"
                    type="text"
                    placeholder="Acme Corp"
                    className="block w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-brand-red focus:ring-4 focus:ring-brand-red/10 transition-all font-medium"
                  />
                </div>
                {errors.company && <p className="text-brand-red text-xs mt-1 ml-1">{errors.company.message}</p>}
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <label htmlFor="contact-phone" className="text-sm font-semibold text-gray-300 ml-1">Phone Number</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" aria-hidden="true">
                    <Phone className="h-5 w-5 text-gray-500" />
                  </div>
                  <input
                    {...register('phone', { required: 'Phone is required' })}
                    id="contact-phone"
                    type="tel"
                    placeholder="(555) 000-0000"
                    className="block w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-brand-red focus:ring-4 focus:ring-brand-red/10 transition-all font-medium"
                  />
                </div>
                {errors.phone && <p className="text-brand-red text-xs mt-1 ml-1">{errors.phone.message}</p>}
              </div>

              {/* Assets (Full Width) */}
              <div className="space-y-2 md:col-span-2">
                <label htmlFor="contact-assets" className="text-sm font-semibold text-gray-300 ml-1">Approx. # of Assets Under Management</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" aria-hidden="true">
                    <Wrench className="h-5 w-5 text-gray-500" />
                  </div>
                  <input
                    {...register('assets')}
                    id="contact-assets"
                    type="number"
                    placeholder="50"
                    className="block w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-brand-red focus:ring-4 focus:ring-brand-red/10 transition-all font-medium [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  />
                </div>
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
                className="w-full bg-brand-red hover:bg-[#b81434] text-white font-bold py-5 rounded-xl transition-all shadow-lg shadow-brand-red/20 flex items-center justify-center space-x-3 group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <div role="status" className="animate-spin h-5 w-5 border-2 border-white/30 border-t-white rounded-full">
                    <span className="sr-only">Submitting...</span>
                  </div>
                ) : (
                  <>
                    <span className="text-lg">Send Inquiry</span>
                    <Send className="h-5 w-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  )
}

// --- Main Page Component ---
export default function Home() {
  const [assets, setAssets] = useState<string>('50')
  const [hoursPerService, setHoursPerService] = useState<string>('1.5')
  const [employeeRate, setEmployeeRate] = useState<string>('30')
  const [billableRate, setBillableRate] = useState<string>('150')

  const numAssets = Math.max(0, parseFloat(assets) || 0)
  const numHoursPerService = Math.max(0, parseFloat(hoursPerService) || 0)
  const numEmployeeRate = Math.max(0, parseFloat(employeeRate) || 0)
  const numBillableRate = Math.max(0, parseFloat(billableRate) || 0)

  const totalMonthlyHours = numAssets * numHoursPerService
  const monthlyLaborSavings = totalMonthlyHours * numEmployeeRate
  const annualLaborSavings = monthlyLaborSavings * 12
  const monthlyRevenue = totalMonthlyHours * numBillableRate
  const annualRevenue = monthlyRevenue * 12
  const totalAnnualImpact = annualLaborSavings + annualRevenue

  const containerVariants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  } as const

  const itemVariants = {
    hidden: {
      y: 20,
      opacity: 0,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 50,
      },
    },
  }

  return (
    <div className="min-h-screen bg-brand-light font-montserrat text-brand-dark selection:bg-brand-red/20 selection:text-brand-dark">
      <Head>
        <title>ROI Calculator</title>
        <meta name="description" content="Calculate the ROI potential of CurbTek for your business. See labor savings and revenue impact instantly." />
      </Head>
      <Toaster position="top-center" richColors />
      {/* Hero Section */}
      <header className="relative bg-brand-dark pt-16 pb-48 overflow-hidden">
        {/* Abstract Background Pattern */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <svg
            className="h-full w-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
          </svg>
          <div className="absolute inset-0 bg-[url('/noise.svg')] opacity-20"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Motion.div
            initial={{
              opacity: 0,
              y: -20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.6,
            }}
          >
            {/* Logo */}
            <img
              src="/curbtek-logo.png"
              alt="CurbTek Logo"
              className="h-20 md:h-24 mx-auto mb-8 object-contain"
            />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-6">
              Calculate Your{' '}
              <span className="text-brand-red">ROI Potential</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Discover the measurable impact CurbTek has on labor efficiency and revenue growth.
            </p>
          </Motion.div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative -mt-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-brand-light">
          <div className="grid grid-cols-1 lg:grid-cols-12 min-h-150">
            {/* Left Column: Inputs */}
            <div className="lg:col-span-5 p-8 md:p-12 bg-white flex flex-col border-b lg:border-b-0 lg:border-r border-brand-light">
              <div className="space-y-8">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="p-2 bg-brand-light rounded-lg">
                      <Activity className="h-5 w-5 text-brand-gray" />
                    </div>
                    <h2 className="text-2xl font-bold text-brand-dark">
                      Your Service Metrics
                    </h2>
                  </div>
                  <p className="text-brand-gray">
                    Enter your current operational parameters.
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Assets Input */}
                  <div className="group">
                    <label
                      htmlFor="assets"
                      className="block text-sm font-semibold text-brand-gray mb-2 group-focus-within:text-brand-red transition-colors"
                    >
                      Assets Serviced Per Month
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" aria-hidden="true">
                        <Wrench className="h-5 w-5 text-brand-gray/50 group-focus-within:text-brand-red transition-colors" />
                      </div>
                      <input
                        type="number"
                        inputMode="numeric"
                        id="assets"
                        className="block w-full pl-12 pr-4 py-4 bg-brand-light/50 border-2 border-brand-light rounded-xl text-brand-dark placeholder-brand-gray/50 focus:outline-none focus:border-brand-red focus:ring-4 focus:ring-brand-red/10 transition-all text-lg font-medium [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        placeholder="50"
                        value={assets}
                        onChange={(e) => setAssets(e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>

                  {/* Hours Per Service Input */}
                  <div className="group">
                    <label
                      htmlFor="hours"
                      className="block text-sm font-semibold text-brand-gray mb-2 group-focus-within:text-brand-red transition-colors"
                    >
                      Estimated Hours Per Service (including drive and wait time)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" aria-hidden="true">
                        <Clock className="h-5 w-5 text-brand-gray/50 group-focus-within:text-brand-red transition-colors" />
                      </div>
                      <input
                        type="number"
                        inputMode="decimal"
                        id="hours"
                        className="block w-full pl-12 pr-4 py-4 bg-brand-light/50 border-2 border-brand-light rounded-xl text-brand-dark placeholder-brand-gray/50 focus:outline-none focus:border-brand-red focus:ring-4 focus:ring-brand-red/10 transition-all text-lg font-medium [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        placeholder="2.0"
                        value={hoursPerService}
                        onChange={(e) => setHoursPerService(e.target.value)}
                        min="0"
                        step="0.1"
                      />
                    </div>
                  </div>

                  {/* Employee Rate Input */}
                  <div className="group">
                    <label
                      htmlFor="employeeRate"
                      className="block text-sm font-semibold text-brand-gray mb-2 group-focus-within:text-brand-red transition-colors"
                    >
                      Average Employee Hourly Rate
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" aria-hidden="true">
                        <span className="text-lg font-semibold text-brand-gray/50 group-focus-within:text-brand-red transition-colors">$</span>
                      </div>
                      <input
                        type="number"
                        inputMode="numeric"
                        id="employeeRate"
                        className="block w-full pl-12 pr-4 py-4 bg-brand-light/50 border-2 border-brand-light rounded-xl text-brand-dark placeholder-brand-gray/50 focus:outline-none focus:border-brand-red focus:ring-4 focus:ring-brand-red/10 transition-all text-lg font-medium [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        placeholder="30"
                        value={employeeRate}
                        onChange={(e) => setEmployeeRate(e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>

                  {/* Billable Rate Input */}
                  <div className="group">
                    <label
                      htmlFor="billableRate"
                      className="block text-sm font-semibold text-brand-gray mb-2 group-focus-within:text-brand-red transition-colors"
                    >
                      Your Billable Rate
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" aria-hidden="true">
                        <span className="text-lg font-semibold text-brand-gray/50 group-focus-within:text-brand-red transition-colors">$</span>
                      </div>
                      <input
                        type="number"
                        inputMode="numeric"
                        id="billableRate"
                        className="block w-full pl-12 pr-4 py-4 bg-brand-light/50 border-2 border-brand-light rounded-xl text-brand-dark placeholder-brand-gray/50 focus:outline-none focus:border-brand-red focus:ring-4 focus:ring-brand-red/10 transition-all text-lg font-medium [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        placeholder="150"
                        value={billableRate}
                        onChange={(e) => setBillableRate(e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Summary Pill */}
                <div className="pt-4">
                  <div className="inline-flex items-center px-4 py-2.5 rounded-full bg-brand-dark/5 text-brand-gray text-sm font-semibold">
                    <Activity className="h-4 w-4 mr-2 text-brand-red" />
                    {numAssets} assets × {numHoursPerService} hrs ={' '}
                    {totalMonthlyHours.toLocaleString()} total hours/month
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Results */}
            <div className="lg:col-span-7 p-8 md:p-12 bg-brand-light/30 flex flex-col justify-center">
              <Motion.div
                className="space-y-8"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* Group 1: Labor Savings */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <div className="p-1.5 bg-brand-dark/10 rounded-md">
                        <PiggyBank className="h-4 w-4 text-brand-dark" />
                      </div>
                      <h3 className="text-sm font-bold text-brand-dark uppercase tracking-wider">
                        Labor Savings
                      </h3>
                    </div>

                    <Motion.div
                      variants={itemVariants}
                      className="bg-white p-5 rounded-2xl border border-brand-light shadow-sm border-l-4 border-l-brand-gray"
                    >
                      <p className="text-xs font-bold text-brand-gray/70 uppercase mb-1">
                        Monthly Savings
                      </p>
                      <div className="text-2xl font-bold text-brand-dark">
                        <AnimatedNumber value={monthlyLaborSavings} />
                      </div>
                    </Motion.div>

                    <Motion.div
                      variants={itemVariants}
                      className="bg-white p-5 rounded-2xl border border-brand-light shadow-sm border-l-4 border-l-brand-gray"
                    >
                      <p className="text-xs font-bold text-brand-gray/70 uppercase mb-1">
                        Annual Savings
                      </p>
                      <div className="text-3xl font-bold text-brand-dark">
                        <AnimatedNumber value={annualLaborSavings} />
                      </div>
                    </Motion.div>
                  </div>

                  {/* Group 2: Revenue Potential */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <div className="p-1.5 bg-brand-red/10 rounded-md">
                        <TrendingUp className="h-4 w-4 text-brand-red" />
                      </div>
                      <h3 className="text-sm font-bold text-brand-dark uppercase tracking-wider">
                        Revenue Potential
                      </h3>
                    </div>

                    <Motion.div
                      variants={itemVariants}
                      className="bg-white p-5 rounded-2xl border border-brand-light shadow-sm border-l-4 border-l-brand-red"
                    >
                      <p className="text-xs font-bold text-brand-gray/70 uppercase mb-1">
                        Monthly Revenue
                      </p>
                      <div className="text-2xl font-bold text-brand-dark">
                        <AnimatedNumber value={monthlyRevenue} />
                      </div>
                    </Motion.div>

                    <Motion.div
                      variants={itemVariants}
                      className="bg-white p-5 rounded-2xl border border-brand-light shadow-sm border-l-4 border-l-brand-red"
                    >
                      <p className="text-xs font-bold text-brand-gray/70 uppercase mb-1">
                        Annual Revenue
                      </p>
                      <div className="text-3xl font-bold text-brand-dark">
                        <AnimatedNumber value={annualRevenue} />
                      </div>
                    </Motion.div>
                  </div>
                </div>

                {/* Total Impact Hero Card */}
                <Motion.div
                  variants={itemVariants}
                  className="relative overflow-hidden rounded-3xl bg-linear-to-br from-brand-dark to-[#1a1a1a] text-white shadow-xl shadow-brand-dark/30 mt-8"
                >
                  <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-brand-red opacity-10 rounded-full blur-3xl"></div>
                  <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-40 h-40 bg-white opacity-5 rounded-full blur-3xl"></div>

                  <div className="relative p-8 md:p-10">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-4 opacity-80">
                          <span className="text-sm font-bold uppercase tracking-widest">
                            Total Annual Impact
                          </span>
                          <ArrowRight className="h-4 w-4" />
                        </div>

                        <div className="text-5xl md:text-6xl font-extrabold tracking-tighter mb-2">
                          <AnimatedNumber value={totalAnnualImpact} />
                        </div>

                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 text-sm text-gray-400 font-semibold">
                          <span className="flex items-center">
                            <span className="w-2 h-2 rounded-full bg-brand-gray mr-2"></span>
                            Labor Savings
                          </span>
                          <span className="text-gray-600">+</span>
                          <span className="flex items-center">
                            <span className="w-2 h-2 rounded-full bg-brand-red mr-2"></span>
                            Revenue Potential
                          </span>
                        </div>
                      </div>

                      <div className="hidden md:block">
                        <div className="h-20 w-20 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/10">
                          <Activity className="h-10 w-10 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Motion.div>

                {/* Footer Note */}
                <Motion.div
                  variants={itemVariants}
                  className="text-center md:text-right"
                >
                  <p className="text-xs text-brand-gray italic">
                    *Based on 12 months of consistent service volume.
                  </p>
                </Motion.div>
              </Motion.div>
            </div>
          </div>
        </div>
      </main>

      {/* Lead Generation Section */}
      <ContactForm prefilledAssets={assets} />

      <footer className="bg-brand-dark py-6 text-center text-sm text-gray-400 font-montserrat">
        2026 CurbTek. All rights reserved.
      </footer>
    </div>
  )
}
